import { useLocation, useSearchParams, useNavigate } from "react-router-dom";
import useAuth from "../hooks/useAuth";
import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, ThemeProvider, createTheme } from "@mui/material";
import LanguageSwitcher from "./LanguageSwitcher";
import AuthService from "../services/auth.service";
import useApi from "../hooks/useApi";

const theme = createTheme({
  palette: {
    white: {
      main: "#FFFFFF",
      light: "#FFFFFF",
      dark: "#FFFFFF",
      contrastText: "#FFFFFF",
    },
    dark_yellow: {
      main: "#393D44",
      light: "#393D44",
      dark: "#393D44",
      contrastText: "#FAEF9B",
    },
    dark_white: {
      main: "#393D44",
      light: "#393D44",
      dark: "#393D44",
      contrastText: "#FFFFFF",
    },
    dark: {
      main: "#393D44",
      light: "#393D44",
      dark: "#393D44",
      contrastText: "#393D44",
    },
  },
});

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const token = searchParams.get("token");
  const axiosPrivate = useApi();
  const [user, setUser] = useState(location.state?.user);
  const { setAuth } = useAuth();
  const navigate = useNavigate();
  const [hasFetched, setHasFetched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [apiSuccess, setApiSuccess] = useState(false);
  useEffect(() => {
    let didCancel = false;
    setIsLoading(true);
    const fetchUser = async () => {
      if (token && !hasFetched) {
        setHasFetched(true);
        try {
          const data = await AuthService.getUserByRememberToken(
            token,
            axiosPrivate
          );
          if (data && data.user) {
            setUser(data.user);
            setApiSuccess(!!data.success);
            sessionStorage.setItem("resetUser", JSON.stringify(data.user));
          } else {
            setApiSuccess(false);
          }
        } catch (err) {
          if (!didCancel) {
            setError('resetPassword.error');
            setApiSuccess(false);
          }
          console.log("error fetching user in reset", err);
        } finally {
          if (!didCancel) {
            setIsLoading(false);
          }
        }
      } else {
        setIsLoading(false);
      }
    };
    fetchUser();
    return () => { didCancel = true; };
  }, [token, axiosPrivate, hasFetched]);

  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");

  useEffect(() => {
    setNewPwd("");
    setConfirmPwd("");
  }, [token]);
  
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const { t } = useTranslation('auth');
  const [showNewPwd, setShowNewPwd] = useState(false);
  const [showConfirmPwd, setShowConfirmPwd] = useState(false);

  // Handle form submission - moved outside conditional blocks
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    
    if (newPwd !== confirmPwd) {
      setError(t('resetPassword.passwordsDontMatch'));
      return;
    }
    
    const payload = {
      token,
      password: newPwd,
      password_confirmation: confirmPwd,
    };
    
    try {
      const data = await AuthService.passwordResetByToken(
        payload,
        axiosPrivate
      );
      
      if (data.success) {
        setSuccess(data.message || t('resetPassword.success'));
        
        if (user?.email) {
          try {
            const loginData = await AuthService.login(
              { email: user.email, password: newPwd },
              axiosPrivate
            );
            
            if (loginData?.token) {
              localStorage.setItem("accessToken", loginData.token);
              setAuth({
                user: loginData.user,
                roles: loginData.user?.roles,
                accessToken: loginData.token,
                refresh: loginData.refresh_token,
                active:
                  loginData.user?.status === "ACTIVE" ||
                  loginData.user?.status === "PENDING_PHONE_VERIFICATION",
                status: loginData.user?.status,
              });
              navigate("/", { replace: true });
            } else {
              setError(t('resetPassword.loginFailed'));
            }
          } catch (loginErr) {
            setError(t('resetPassword.loginFailed'));
          }
        } else {
          navigate("/", { replace: true });
        }
      } else {
        setError(data.error || t('resetPassword.error'));
      }
    } catch (err) {
      setError(err?.response?.data?.error || t('resetPassword.error'));
    }
  };

  if (isLoading) {
    return (
      <div className="main-login">
        <div className="container">
          <div className="admin-left">
            <div className="logo">Flyspa</div>
            <h2>{t('resetPassword.title')}</h2>
            <div style={{
              background: "#fff",
              borderRadius: "18px",
              boxShadow: "0 2px 16px rgba(0,0,0,0.08)",
              padding: "32px 24px",
              margin: "32px auto",
              maxWidth: "400px",
              textAlign: "center",
              fontSize: "18px",
              color: "#393D44",
              fontWeight: "500",
            }}>
              <p style={{ margin: 0 }}>{t('resetPassword.loading')}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!token || !user) {
    return (
      <div className="main-login">
        <div className="container">
          <div className="admin-left">
            <div className="logo">Flyspa</div>
            <h2>{t('resetPassword.title')}</h2>
            {error ? (
              <div style={{
                background: "#fff",
                borderRadius: "18px",
                boxShadow: "0 2px 16px rgba(0,0,0,0.08)",
                padding: "32px 24px",
                margin: "32px auto",
                maxWidth: "400px",
                textAlign: "center",
                fontSize: "18px",
                color: "#393D44",
                fontWeight: "500",
              }}>
                <p style={{ margin: 0 }}>{t(error)}</p>
              </div>
            ) : (
              <div style={{
                background: "#fff",
                borderRadius: "18px",
                boxShadow: "0 2px 16px rgba(0,0,0,0.08)",
                padding: "32px 24px",
                margin: "32px auto",
                maxWidth: "400px",
                textAlign: "center",
                fontSize: "18px",
                color: "#393D44",
                fontWeight: "500",
              }}>
                <p style={{ margin: 0 }}>{t('resetPassword.info')}</p>
              </div>
            )}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 5,
              }}
            >
              <ThemeProvider theme={theme}>
                <Button
                  variant="outlined"
                  color="white"
                  type="button"
                  onClick={() => (window.location.href = "/")}
                  sx={{
                    textTransform: "none",
                    borderRadius: "15px",
                    width: "100%",
                    maxWidth: "100%",
                    minHeight: "57px",
                  }}
                >
                  {t('resetPassword.backToHome')}
                </Button>
              </ThemeProvider>
              <LanguageSwitcher />
            </Box>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-login">
      <div className="container">
        <div className="admin-left">
          <div className="logo">Flyspa</div>
          <h2>{t('resetPassword.title')}</h2>
          <form
            className="reset-password-form"
            style={{ width: "100%" }}
            onSubmit={handleSubmit}
          >
            {error && <p className="error">{error}</p>}
            {success && <p className="success">{success}</p>}
            <div className="form-text" style={{ position: 'relative' }}>
              <input
                type={showNewPwd ? "text" : "password"}
                value={newPwd}
                onChange={(e) => setNewPwd(e.target.value)}
                placeholder={t('resetPassword.newPassword')}
                className="password"
                required
                style={{ paddingRight: 40 }}
              />
              <span
                onClick={() => setShowNewPwd((v) => !v)}
                style={{
                  position: 'absolute',
                  right: 10,
                  top: '42%',
                  transform: 'translateY(-50%)',
                  cursor: 'pointer',
                  zIndex: 2,
                  marginRight: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  height: '24px',
                }}
                tabIndex={0}
                aria-label={showNewPwd ? t('resetPassword.hidePassword') : t('resetPassword.showPassword')}
              >
                {showNewPwd ? (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#393D44" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{overflow: 'visible'}}><path d="M17.94 17.94A10.06 10.06 0 0 1 12 20c-5 0-9.27-3.11-11-8 1.21-3.06 3.62-5.5 6.64-6.71"/><path d="M1 1l22 22"/><path d="M9.53 9.53A3.5 3.5 0 0 0 12 15.5c1.93 0 3.5-1.57 3.5-3.5 0-.47-.09-.92-.26-1.33"/></svg>
                ) : (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#393D44" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{overflow: 'visible'}}><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3.5"/></svg>
                )}
              </span>
            </div>
            <div className="form-text" style={{ position: 'relative' }}>
              <input
                type={showConfirmPwd ? "text" : "password"}
                value={confirmPwd}
                onChange={(e) => setConfirmPwd(e.target.value)}
                placeholder={t('resetPassword.confirmNewPassword')}
                className="password"
                required
                style={{ paddingRight: 40 }}
              />
              <span
                onClick={() => setShowConfirmPwd((v) => !v)}
                style={{
                  position: 'absolute',
                  right: 10,
                  top: '42%',
                  transform: 'translateY(-50%)',
                  cursor: 'pointer',
                  zIndex: 2,
                  marginRight: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  height: '24px',
                }}
                tabIndex={0}
                aria-label={showConfirmPwd ? t('resetPassword.hidePassword') : t('resetPassword.showPassword')}
              >
                {showConfirmPwd ? (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#393D44" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{overflow: 'visible'}}><path d="M17.94 17.94A10.06 10.06 0 0 1 12 20c-5 0-9.27-3.11-11-8 1.21-3.06 3.62-5.5 6.64-6.71"/><path d="M1 1l22 22"/><path d="M9.53 9.53A3.5 3.5 0 0 0 12 15.5c1.93 0 3.5-1.57 3.5-3.5 0-.47-.09-.92-.26-1.33"/></svg>
                ) : (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#393D44" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{overflow: 'visible'}}><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3.5"/></svg>
                )}
              </span>
            </div>
            <div className="form-submit">
              <ThemeProvider theme={theme}>
                <Button
                  variant="outlined"
                  color="white"
                  type="submit"
                  sx={{
                    textTransform: "none",
                    borderRadius: "15px",
                    width: "100%",
                    maxWidth: "100%",
                    minHeight: "57px",
                  }}
                >
                  {t('resetPassword.resetPasswordButton')}
                </Button>
              </ThemeProvider>
            </div>
          </form>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 5,
            }}
          >
            <ThemeProvider theme={theme}>
              <Button
                variant="outlined"
                color="white"
                type="button"
                onClick={() => (window.location.href = "/")}
                sx={{
                  textTransform: "none",
                  borderRadius: "15px",
                  width: "100%",
                  maxWidth: "100%",
                  minHeight: "57px",
                }}
              >
                {t('resetPassword.backToHome')}
              </Button>
            </ThemeProvider>
            <LanguageSwitcher />
          </Box>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;